'use client'

import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react'
import { useSnapshot, subscribe } from 'valtio'
import * as Y from 'yjs'
import { CustomWebsocketProvider } from '~/components/YJSDocument/YJSDocumentProvider/CustomWebsocketProvider'
import ProviderManager from '~/components/YJSDocument/YJSDocumentProvider/ProviderManager'
import {
  EvaluationDataTable,
  EvaluationContextState,
  evaluationStore,
  resetEvaluationStore,
  GateValue,
  useTableSnapshot,
  bindEvaluationStoreTableToYMap,
  bindEvaluationStoreObjectToYObject,
  bindChangeLogsToYDoc,
  bindDataTablesSlateToYDoc,
  useUpdateEventMessageRelatedState,
  useSetIsRestoring,
  useSetIsRepairing,
  useSetIsCollaborating,
  useAddPhantomDeleteFailure,
  useInProcessUpdates,
  useResetEventAtoms,
  useRecoverRequested,
} from '../evaluation-store'
import { CustomDoc } from '~/components/YJSDocument/YJSDocumentProvider/CustomDoc'
import { CommentUser, TComment } from '@udecode/plate-comments'
import { IndexeddbPersistence } from 'y-indexeddb'
import { useDashboardWorkspaceContext } from '~/app/(dashboard)/dashboardContext'
import * as encoding from 'lib0/encoding'
import { safeYTextToSlateElement } from '~/components/TaloEditor/sanitizeSlateElements'

// Polling interval for hash-based convergence check (ms)
const HASH_POLL_INTERVAL_MS = 30_000

export const EvaluationContext = createContext<
  EvaluationContextState | undefined
>(undefined)

type EvaluationProviderProps = {
  children: ReactNode
  roomName?: string
  evaluationId?: string
  loadStartTime?: number | null
  loadStartTimeRef?: React.MutableRefObject<number | null>
  graphqlQueryTime?: number
  hasRecordedLoadTimeRef?: React.MutableRefObject<boolean>
  loadSessionId?: string | null
  loadSessionIdRef?: React.MutableRefObject<string | null>
}

const messageRepairEvaluation = 43
const messageRequestRecover = 54

let isPendingRepair = false

const sendRepairEvaluation = (ws) => {
  console.log('Preparing to send Repair')
  const encoder = encoding.createEncoder()
  encoding.writeVarUint(encoder, messageRepairEvaluation)
  const message = encoding.toUint8Array(encoder)
  encoding.writeVarString(encoder, 'ignore')
  ws.send(message)
  isPendingRepair = true
  console.log('Sent repair evaluation')
}

/**
 * Send a recover request to the server. The server will:
 * 1. Trigger file-loader repair (rebuild Y.Doc from current_evaluations/DB)
 * 2. Send messageRepairing to show "Repairing..." in UI
 * 3. Send messageDamaged which triggers handleDamagedReset on the client
 */
const sendRecoverRequest = (ws: WebSocket) => {
  console.log('[Recover] Sending recover request to server')
  const encoder = encoding.createEncoder()
  encoding.writeVarUint(encoder, messageRequestRecover)
  ws.send(encoding.toUint8Array(encoder))
  console.log('[Recover] Recover request sent')
}

const updateTables = (
  provider: CustomWebsocketProvider,
  doc: Y.Doc,
  tableName: string
) => {
  if (
    doc &&
    provider.ws &&
    provider.ws.readyState === WebSocket.OPEN &&
    provider.synced
  ) {
    // Only access the map if it exists in share to avoid Y.js warnings
    const table = doc.share.has(tableName) ? doc.getMap(tableName) : null
    let data: Y.Array<any> | null = null
    if (table instanceof Y.Map) {
      const dataItem = table.get('data')
      if (dataItem instanceof Y.Array) {
        data = dataItem
      }
    }
    if (data && data.length === 0) {
      if (!isPendingRepair) {
        console.debug(`[Table] ${tableName} is empty`)
        // sendRepairEvaluation(provider.ws);  put back in a bit.
      }
    } else {
      if (isPendingRepair) {
        console.debug(`[Table] ${tableName} has data, resetting repair flag`)
        isPendingRepair = false
      }
    }
  } else {
    console.debug(
      `Not ready to check table ${tableName}:`,
      `WS Open: ${provider.ws?.readyState === WebSocket.OPEN},`,
      `Synced: ${provider.synced},`
    )
    return false
  }
  return true
}

export const EvaluationProvider = ({
  children,
  roomName,
  evaluationId,
  loadStartTime,
  loadStartTimeRef,
  graphqlQueryTime,
  hasRecordedLoadTimeRef,
  loadSessionId,
  loadSessionIdRef,
}: EvaluationProviderProps) => {
  const [provider, setProvider] = useState<CustomWebsocketProvider | null>(null)
  const [doc, setDoc] = useState<CustomDoc | null>(null)
  const [persistenceInitialized, setPersistenceInitialized] = useState(false)
  const [resetCounter, setResetCounter] = useState(0)
  const [persistenceSynced, setPersistenceSynced] = useState(false)
  // When true, children (Slate editor) are unmounted to prevent removeChild race conditions
  // during a damaged reset. Set to true at the start of handleDamagedReset, cleared on sync.
  const [isDamagedResetting, setIsDamagedResetting] = useState(false)
  const dashboardContext = useDashboardWorkspaceContext()
  const memberId = dashboardContext.memberId
  const updateEventMessageRelatedState = useUpdateEventMessageRelatedState()
  const setIsRestoring = useSetIsRestoring()
  const setIsRepairing = useSetIsRepairing()
  const setIsCollaborating = useSetIsCollaborating()
  const addPhantomDeleteFailure = useAddPhantomDeleteFailure()
  const inProcessUpdates = useInProcessUpdates()
  const resetEventAtoms = useResetEventAtoms()
  // Track if this is the first sync (initial load) - use a ref to persist across re-renders
  const isFirstSyncedRef = useRef(true)
  // Track create end recording to prevent duplicates
  const hasRecordedCreateEndRef = useRef(false)
  // Track whether a damaged reset is in progress (so the sync event can clear restoring state)
  const isRestoringRef = useRef(false)
  // Re-entrancy guard: ignore duplicate damaged signals while a reset is already in flight
  const isDamagedResettingRef = useRef(false)
  const setIsDamagedResettingRef = useRef(setIsDamagedResetting)
  // Track whether the current restore was triggered by a real in-flight edit that was reverted.
  // Only true when damaged signal arrived while updates were in-flight; false for stale-IDB restores.
  const isDamagedResetRef = useRef(false)

  // Refs for damaged reset — allow handleDamagedReset (no deps) to recreate doc/provider
  const roomNameRef = useRef<string | undefined>(undefined)
  roomNameRef.current = roomName
  const memberIdRef = useRef<string | undefined>(undefined)
  memberIdRef.current = memberId
  const setDocRef = useRef(setDoc)
  setDocRef.current = setDoc
  const setProviderRef = useRef(setProvider)
  setProviderRef.current = setProvider
  const setPersistenceSyncedRef = useRef(setPersistenceSynced)
  setPersistenceSyncedRef.current = setPersistenceSynced

  // Use refs to store current values so callbacks don't need them in deps
  const providerRef = useRef<CustomWebsocketProvider | null>(null)
  providerRef.current = provider
  const updateEventMessageRelatedStateRef = useRef(
    updateEventMessageRelatedState
  )
  updateEventMessageRelatedStateRef.current = updateEventMessageRelatedState
  const setIsRestoringRef = useRef(setIsRestoring)
  setIsRestoringRef.current = setIsRestoring
  // Wrap setIsRepairing to also update a ref so interval callbacks can read the current value
  const isRepairingRef = useRef(false)
  const setIsRepairingWrapped = useCallback((value: boolean) => {
    isRepairingRef.current = value
    setIsRepairing(value)
  }, [setIsRepairing])
  const setIsRepairingRef = useRef(setIsRepairingWrapped)
  setIsRepairingRef.current = setIsRepairingWrapped
  const setIsCollaboratingRef = useRef(setIsCollaborating)
  setIsCollaboratingRef.current = setIsCollaborating
  // Prevents the consistency check from running twice if both the early handler
  // (registered before connect()) and the main useEffect handler both fire.
  const hasRunConsistencyCheckRef = useRef(false)
  // Safety timeout: if the server never responds to the consistency check,
  // release isCollaborating after this many ms so the UI doesn't stay frozen.
  const collaboratingSafetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const repairingSafetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const addPhantomDeleteFailureRef = useRef(addPhantomDeleteFailure)
  addPhantomDeleteFailureRef.current = addPhantomDeleteFailure
  const resetEventAtomsRef = useRef(resetEventAtoms)
  resetEventAtomsRef.current = resetEventAtoms
  const inProcessUpdatesRef = useRef(inProcessUpdates)
  inProcessUpdatesRef.current = inProcessUpdates

  // Ref to the hash polling interval timer, cleared on unmount
  const hashPollIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleHardReset = useCallback(async () => {
    const currentProvider = providerRef.current
    if (!currentProvider) return

    console.log('[HardReset] Starting — creating fresh Y.Doc to avoid CRDT tombstone poisoning (v2)')

    const currentRoom = roomNameRef.current
    const currentMemberId = memberIdRef.current
    const docGuid = currentProvider.doc.guid

    // Destroy the old provider completely (disconnects + removes from registry)
    if (currentRoom) {
      ProviderManager.resetProvider(currentRoom)
    } else {
      currentProvider.disconnect()
    }

    // Clear IndexedDB so stale state can't replay on reconnect
    try {
      const existingPersistence = (currentProvider as any)._persistence as IndexeddbPersistence | null
      if (existingPersistence) {
        await existingPersistence.clearData()
      } else {
        const idb = new IndexeddbPersistence(docGuid, currentProvider.doc)
        await idb.clearData()
        idb.destroy()
      }
      console.log('[HardReset] IndexedDB cleared')
    } catch (err) {
      console.error('[HardReset] Error clearing IndexedDB:', err)
    }

    // Reset the Valtio store
    resetEvaluationStore()

    // Create a FRESH Y.Doc and provider.
    // CRITICAL: do NOT reuse or clear the old Y.Doc. Calling value.clear() on
    // the old doc creates CRDT tombstones that propagate to the server on
    // reconnect, permanently deleting keys for all clients. A fresh Y.Doc has
    // no CRDT history and no tombstones.
    if (currentRoom && currentMemberId) {
      const newDoc = new CustomDoc(currentRoom, currentRoom)
      isFirstSyncedRef.current = true
      setPersistenceSyncedRef.current(false)
      setDocRef.current(newDoc)
      const newProvider = ProviderManager.getProvider(currentRoom, currentMemberId, newDoc)
      setProviderRef.current(newProvider)
      newDoc.registerProvider(newProvider)
      newProvider.connect()
      console.log('[HardReset] Created fresh Y.Doc and provider, reconnecting')
    } else {
      // Fallback: reconnect old provider (shouldn't happen in normal use)
      currentProvider.connect()
      console.log('[HardReset] Reconnecting existing provider (no roomName/memberId)')
    }
  }, []) // No dependencies - uses ref to get current provider

  // No-op placeholder — repair buffer observer removed (NEVER_DELETEABLE concept removed)

  /**
   * Damaged evaluation reset: clears IndexedDB, destroys the old provider and Y.Doc,
   * and creates a fresh Y.Doc + provider to reconnect from a clean state.
   *
   * Why create a FRESH Y.Doc (not just clear IDB and reconnect):
   * The old Y.Doc may retain stale CRDT items. On reconnect, the server's newer items
   * win CRDT conflicts — data-observer sees these as phantom deletes → sends
   * messageDamaged → infinite loop.
   *
   * A fresh Y.Doc has no CRDT history, so there are no stale items to conflict with
   * the server's state. It also creates NO tombstones, so there is no risk of
   * accidentally deleting server-side data during the reconnect sync.
   */
  const handleDamagedReset = useCallback(async () => {
    const currentProvider = providerRef.current
    if (!currentProvider) return

    // Re-entrancy guard: if a reset is already running, ignore the duplicate signal.
    // Multiple damaged signals can arrive in quick succession (e.g. phantom-delete),
    // and running two resets concurrently causes the same removeChild race we're fixing.
    if (isDamagedResettingRef.current) {
      console.warn('[DamagedReset] Already in progress — ignoring duplicate damaged signal')
      return
    }
    isDamagedResettingRef.current = true

    console.warn('[State] → DAMAGED_RESETTING (CRDT stale — clearing IDB, rebuilding from y-websocket)', {
      inFlightEdits: inProcessUpdatesRef.current.size,
    })

    // Clear repairing state — we're now in the restoring phase
    setIsRepairingRef.current(false)
    // Signal the UI that a restore is in progress.
    // Only flag as "edit reverted" if there were in-flight user edits when the damaged signal
    // arrived. If no edits were in-flight (e.g. stale IDB on fresh page load), we restore
    // silently without showing "Update failed" — the restore itself is the success.
    isRestoringRef.current = true
    isDamagedResetRef.current = inProcessUpdatesRef.current.size > 0
    setIsRestoringRef.current(true)

    // Stop hash polling during repair
    if (hashPollIntervalRef.current) {
      clearInterval(hashPollIntervalRef.current)
      hashPollIntervalRef.current = null
    }

    // Unmount children (Slate editor) BEFORE any state mutations.
    // resetEvaluationStore() uses Valtio's useSyncExternalStore which fires synchronously,
    // racing with React renders queued by disconnect() events and causing removeChild errors
    // in Slate's DOM tree. Unmounting first ensures there is no active Slate tree to conflict.
    setIsDamagedResettingRef.current(true)

    // Yield one macrotask so React commits the children unmount before we continue.
    await new Promise<void>((resolve) => setTimeout(resolve, 0))

    // Destroy the old provider completely. ProviderManager.resetProvider disconnects,
    // destroys, and removes it from the registry so a fresh one can be created below.
    const currentRoom = roomNameRef.current
    const currentMemberId = memberIdRef.current
    const docGuid = currentProvider.doc.guid
    const docForIdb = currentProvider.doc

    if (currentRoom) {
      ProviderManager.resetProvider(currentRoom)
    } else {
      currentProvider.disconnect()
    }

    // Clear IndexedDB: removes the stale persisted state so it can't replay on reconnect.
    try {
      const existingPersistence = (currentProvider as any)._persistence as IndexeddbPersistence | null
      if (existingPersistence) {
        await existingPersistence.clearData()
      } else {
        const idb = new IndexeddbPersistence(docGuid, docForIdb)
        await idb.clearData()
        idb.destroy()
      }
      console.log('[DamagedReset] IndexedDB cleared')
    } catch (err) {
      console.error('[DamagedReset] Error clearing IndexedDB:', err)
    }

    // Reset the Valtio store so the UI reflects the incoming server state.
    // Safe now: Slate children are unmounted, no DOM conflict possible.
    resetEvaluationStore()

    // Create a FRESH Y.Doc and provider.
    //
    // CRITICAL: we must NOT reuse the old Y.Doc. Stale CRDT items would lose conflicts
    // on reconnect — data-observer sees these as phantom deletes → sends messageDamaged
    // → infinite damaged-reset loop.
    //
    // A fresh Y.Doc has no CRDT history — no stale items, no phantom deletes, no loop.
    if (currentRoom && currentMemberId) {
      const newDoc = new CustomDoc(currentRoom, currentRoom)
      isFirstSyncedRef.current = true
      setPersistenceSyncedRef.current(false)
      setDocRef.current(newDoc)
      const newProvider = ProviderManager.getProvider(currentRoom, currentMemberId, newDoc)
      setProviderRef.current(newProvider)
      newDoc.registerProvider(newProvider)

      // Attach early sync handler BEFORE connect() to close the race where sync completes
      // before the useEffect re-runs and attaches handlers to the new provider.
      // Without this: setIsDamagedResettingRef never called → children stay unmounted →
      // blank page until the 30s safety timeout.
      newProvider.once('sync', (isSynced: boolean) => {
        if (isSynced && isRestoringRef.current) {
          console.log('[State] → SYNCED (early sync handler: damaged reset complete)')
          isRestoringRef.current = false
          setIsRestoringRef.current(false)

          if (isDamagedResetRef.current) {
            isDamagedResetRef.current = false
            // Show "update failed" so user knows their in-flight change was discarded
            addPhantomDeleteFailureRef.current()
          }
          setIsDamagedResettingRef.current(false)
          isDamagedResettingRef.current = false
        }
      })

      newProvider.connect()
      console.log('[DamagedReset] Created fresh Y.Doc and provider, reconnecting')
    } else {
      // Fallback: reconnect old provider (shouldn't happen in normal use)
      currentProvider.connect()
      console.log('[DamagedReset] Reconnecting existing provider (no roomName/memberId)')
    }

    // Safety timeout: if the sync event never fires (e.g., y-websocket unreachable),
    // clear all reset state after 30 seconds so the UI doesn't stay blank forever.
    setTimeout(() => {
      if (isRestoringRef.current) {
        console.warn('[DamagedReset] Restoring timed out after 30s — clearing restoring state')
        isRestoringRef.current = false
        setIsRestoringRef.current(false)
        // Don't call addPhantomDeleteFailure here — the update may still arrive later
      }
      // Re-enable children even if sync never arrived
      setIsDamagedResettingRef.current(false)
      isDamagedResettingRef.current = false
    }, 30_000)
  }, []) // No dependencies - uses ref to get current provider

  // Reset store when roomName or memberId changes (before useEffect to avoid hook order issues)
  // Use a separate effect to reset, ensuring it happens before the main setup effect
  const lastRoomNameRef = useRef<string | undefined>(undefined)
  const lastMemberIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    // Only reset if roomName or memberId actually changed
    if (
      roomName &&
      memberId &&
      (roomName !== lastRoomNameRef.current ||
        memberId !== lastMemberIdRef.current)
    ) {
      console.log('[EvaluationProvider] Resetting store for new room/member:', {
        roomName,
        memberId,
      })
      resetEvaluationStore()
      // Reset Jotai atoms (inProcessUpdates, completedUpdates, failedUpdates, etc.)
      // to prevent stale update IDs from the previous evaluation bleeding through
      resetEventAtoms()
      lastRoomNameRef.current = roomName
      lastMemberIdRef.current = memberId
    }
  }, [roomName, memberId, resetEventAtoms])

  // Core effect to initialize the provider and doc
  // IMPORTANT: DO NOT CONVOLUTE.
  useEffect(() => {
    console.log(
      `[EvaluationProvider] useEffect triggered: roomName=${roomName}, memberId=${
        memberId || '(empty)'
      }`
    )
    if (!roomName) {
      console.log('[EvaluationProvider] Early return: no roomName')
      return
    }
    if (!memberId || memberId === '') {
      console.log('[EvaluationProvider] Early return: no memberId')
      return
    }

    // Check if provider already exists - if so, reuse its doc to prevent unnecessary remounts
    // This prevents doc.guid from changing, which would cause EvaluationEditorProvider to remount
    let doc: CustomDoc
    const existingDoc = ProviderManager.getExistingProviderDoc(roomName)
    if (existingDoc) {
      // Provider exists - reuse its doc to maintain stable guid
      doc = existingDoc
    } else {
      // No provider exists - create new doc
      doc = new CustomDoc(roomName, roomName)
    }

    // Reset persistenceSynced when creating a new provider
    setPersistenceSynced(false)
    setDoc(doc)
    const newProvider = ProviderManager.getProvider(roomName, memberId, doc)
    setProvider(newProvider)

    // Register the provider with the doc so it can verify WebSocket connectivity
    // on every user-originated change (rather than relying solely on the wsconnected flag)
    doc.registerProvider(newProvider)

    // Reset the guard so a fresh consistency check runs for this provider.
    hasRunConsistencyCheckRef.current = false

    // Helper to run the consistency check exactly once per provider lifetime.
    // Extracted so it can be called from either the early-sync path or the
    // already-synced path (cached provider returned by ProviderManager).
    const runConsistencyCheck = () => {
      if (hasRunConsistencyCheckRef.current || isDamagedResettingRef.current) return
      const dataMap = doc.share.has('data') ? doc.getMap('data') : null
      if (!dataMap) return
      hasRunConsistencyCheckRef.current = true
      setIsCollaboratingRef.current(true)
      // If another state took over (restoring/repairing), or WS is gone, release now.
      if (isRestoringRef.current || isDamagedResettingRef.current || !newProvider.wsconnected) {
        setIsCollaboratingRef.current(false)
        return
      }
      // Skip the check if there are in-flight updates — the server's hash would diverge
      // from ours and trigger a false damaged reset. Just release the hold.
      if (inProcessUpdatesRef.current.size > 0) {
        console.log('[ConsistencyCheck] Skipping — in-flight updates present, releasing hold')
        setIsCollaboratingRef.current(false)
        return
      }
      // On initial load the sync gate handles timing (fires after IDB loads).
      // On reconnections (provider already synced) there is no pending gate so we
      // send the check immediately — IDB is already loaded at this point.
      newProvider.sendConsistencyCheck?.()
      // Safety net: if the server never responds (network drop, y-websocket bug, etc.),
      // release the collaborating hold after 15s so the UI doesn't stay frozen.
      if (collaboratingSafetyTimeoutRef.current) {
        clearTimeout(collaboratingSafetyTimeoutRef.current)
      }
      collaboratingSafetyTimeoutRef.current = setTimeout(() => {
        console.warn('[ConsistencyCheck] Safety timeout — server did not respond in 15s, releasing hold')
        setIsCollaboratingRef.current(false)
        collaboratingSafetyTimeoutRef.current = null
      }, 15_000)
    }

    if ((newProvider as any).synced) {
      // Provider is a cached, already-synced instance — run the check immediately
      // since no future 'sync' event will fire for an already-connected provider.
      console.log('[ConsistencyCheck] Provider already synced — running check immediately')
      runConsistencyCheck()
    } else {
      // Fresh provider: register before connect() to close the race where the 'sync'
      // event fires during the React scheduling gap before the main useEffect runs.
      newProvider.once('sync', (isSynced: boolean) => {
        if (isSynced) runConsistencyCheck()
      })
    }

    // Always connect on initial load - the useSyncToggle hook will manage
    // disconnection if the user has previously turned sync off
    newProvider.connect()
    console.log(`Provider initiated websocket connection for room: ${roomName}`)
  }, [roomName, memberId, evaluationId])

  const wsConnected =
    provider?.getFullConnectionStatus?.()?.wsConnected ?? false

  // Core effect to initialize the persistence for the provider
  // IndexedDB should be created regardless of websocket connection status
  // so that changes are persisted locally even when sync is OFF
  // IMPORTANT: DO NOT CONVOLUTE
  useEffect(() => {
    if (!provider || !doc || provider?._persistence) return

    // A healthy IDB for a room that has ever synced will have many update records.
    // Fewer than this threshold in a non-empty DB indicates a partial/broken write.
    const MIN_HEALTHY_UPDATES = 10

    const initializePersistence = async () => {
      try {
        // Health-check the IDB for this specific evaluation.
        //   0 records              → never synced before (empty)           → OK, proceed
        //   ≥ MIN_HEALTHY_UPDATES  → looks like a complete snapshot         → OK, proceed
        //   1 .. MIN_HEALTHY_UPDATES-1 → partial / broken write            → clear, restart fresh
        const count = await new Promise<number>((resolve) => {
          const openReq = indexedDB.open(doc.guid)

          // onupgradeneeded fires only when the DB is brand-new.
          // Abort the upgrade so we do not leave an empty, schema-less DB behind —
          // y-indexeddb will create it properly when IndexeddbPersistence opens it next.
          openReq.onupgradeneeded = () => {
            openReq.transaction?.abort()
          }

          openReq.onsuccess = (e) => {
            const db = (e.target as IDBOpenDBRequest).result
            if (!db.objectStoreNames.contains('updates')) {
              db.close()
              resolve(0)
              return
            }
            const tx = db.transaction('updates', 'readonly')
            const countReq = tx.objectStore('updates').count()
            countReq.onsuccess = () => { db.close(); resolve(countReq.result) }
            countReq.onerror  = () => { db.close(); resolve(0) }
          }

          openReq.onerror = () => resolve(0)
        })

        console.log(`[IDB Health] ${doc.guid}: ${count} update records`)

        if (count > 0 && count < MIN_HEALTHY_UPDATES) {
          console.warn(`[IDB Health] ${doc.guid}: suspicious record count (${count}) — clearing broken IDB`)
          await new Promise<void>((resolve) => {
            const del = indexedDB.deleteDatabase(doc.guid)
            del.onsuccess  = () => resolve()
            del.onerror    = () => resolve() // proceed even if delete fails
            del.onblocked  = () => resolve()
          })
        }
      } catch (err) {
        console.error('[IDB Health] Error during health check:', err)
        // Proceed anyway — better to load with potentially stale data than to block
      }

      provider._persistence = new IndexeddbPersistence(doc.guid, doc)
      setPersistenceInitialized(true)
    }

    initializePersistence()

    return () => {
      if (provider?._persistence) {
        provider._persistence.destroy()
        provider._persistence = null
        setPersistenceInitialized(false)
      }
    }
  }, [provider, doc])

  // Effect dealing with the rest of the old logic
  // TODO: Clean this up when the updates are working correctly
  useEffect(() => {
    if (!persistenceInitialized || !provider?._persistence || !doc || !roomName)
      return

    const persistence = provider?._persistence

    // Check if persistence is already synced (for new evaluations with no IndexedDB data)
    // This can happen if IndexedDB syncs immediately because there's nothing to load
    const checkPersistenceSynced = () => {
      const connectionStatus = provider.getFullConnectionStatus?.()
      const dataMap = doc.share.has('data') ? doc.getMap('data') : null
      const wsConnected = connectionStatus?.wsConnected ?? false
      const wsReadyState = connectionStatus?.wsReadyState ?? null
      const isWebSocketOpen = wsReadyState === WebSocket.OPEN

      if (persistence.synced) {
        // Only mark as synced if WebSocket is actually connected
        if (isWebSocketOpen) {
          console.log('[State] → SYNCED (IDB already synced + WS open)', { docGuid: doc.guid })
          setPersistenceSynced(true)
          return true
        } else {
          console.log('[State] IDB already synced but WS not yet open — Gate 2 waiting', { docGuid: doc.guid })
          return false
        }
      }
      return false
    }

    // Check immediately in case it's already synced
    if (checkPersistenceSynced()) {
      // Already synced, handler will be called below but we've already set state
    }

    // Track if we've set persistenceSynced to avoid duplicate calls
    const persistenceSyncedRef = { current: false }

    // Add IndexedDB persistence logging with connection status
    persistence.on('synced', () => {
      if (persistenceSyncedRef.current) return // Already set
      const connectionStatus = provider.getFullConnectionStatus?.()
      // Only access the map if it exists in share to avoid Y.js warnings
      const dataMap = doc.share.has('data') ? doc.getMap('data') : null

      // CRITICAL: Only mark IndexedDB as synced if the WebSocket is actually connected
      // IndexedDB can load data from the database, but if the WebSocket is disconnected,
      // we shouldn't consider it "synced" because updates won't be sent to the server
      const wsConnected = connectionStatus?.wsConnected ?? false
      const wsReadyState = connectionStatus?.wsReadyState ?? null
      const isWebSocketOpen = wsReadyState === WebSocket.OPEN

      console.log('[State] IDB synced with Y.Doc', {
        docGuid: doc.guid,
        isWebSocketOpen,
        dataMapSize: dataMap ? dataMap.size : 0,
        timestamp: new Date().toISOString(),
      })

      // Only mark as synced if WebSocket is actually connected
      // This prevents the UI from thinking it's connected when it's not
      if (isWebSocketOpen) {
        persistenceSyncedRef.current = true
        setPersistenceSynced(true)
        console.log('[State] → SYNCED (Gate 2 opened: IDB + WS both ready)')
        // Notify the provider that IDB data is now in the Y.Doc so the pre-sync
        // consistency gate can fire (if a server SYNC_STEP_1 is pending).
        provider.onIdbSynced?.()
      } else {
        console.log('[State] IDB synced but WS not yet open — waiting for WS to open Gate 2')
        // Don't set persistenceSynced yet - wait for WebSocket to connect
        // The WebSocket 'status' event handler will set it when connection is established
      }
    })

    // Also check after a short delay in case the event fires very quickly
    // This handles the case where IndexedDB syncs immediately for new evaluations
    // Use a ref to track if we've already set it to avoid race conditions
    const syncedCheckTimeout = setTimeout(() => {
      if (persistence.synced) {
        checkPersistenceSynced()
      }
    }, 100)

    // Fallback timeout: If Gate 2 hasn't opened within 5 seconds for any reason
    // (IDB never fired synced, or IDB synced but WS hasn't connected yet, or
    // completely offline), proceed with whatever state the Y.Doc is in.
    // IDB syncs in milliseconds locally, so !persistence.synced would almost never
    // be true here — the real trigger is "IDB ready but WS not open yet".
    const fallbackTimeout = setTimeout(() => {
      if (!persistenceSyncedRef.current) {
        const connectionStatus = provider.getFullConnectionStatus?.()
        const dataMap = doc.share.has('data') ? doc.getMap('data') : null
        const providerSynced = provider.isDocSynced?.() ?? false
        const hasData = dataMap ? dataMap.size > 0 : false
        console.warn('[State] → SYNCED via fallback (Gate 2: 5s timeout)', {
          docGuid: doc.guid,
          idbSynced: persistence.synced,
          providerSynced,
          hasData,
          wsReadyState: connectionStatus?.wsReadyState,
          timestamp: new Date().toISOString(),
        })
        persistenceSyncedRef.current = true
        setPersistenceSynced(true)
      }
    }, 5000) // 5 second timeout

    // Store timeouts for cleanup
    ;(provider as any)._persistenceSyncedCheckTimeout = syncedCheckTimeout
    ;(provider as any)._persistenceFallbackTimeout = fallbackTimeout

    persistence.on('update', (update: Uint8Array) => {
      console.debug('[IDB] update', { docGuid: doc.guid, updateSize: update.length })
    })

    // Periodic connection status check
    const persistenceCheckInterval = setInterval(() => {
      const connectionStatus = provider.getFullConnectionStatus?.()
      console.debug('[State] periodic check', {
        room: roomName,
        idbSynced: persistence.synced,
        ...connectionStatus,
      })
    }, 10000) // Every 10 seconds

    // Store persistence reference for cleanup
    ;(provider as any)._persistenceCheckInterval = persistenceCheckInterval

    provider.on('hardReset', handleHardReset)

    // Repair in progress — the y-importer repair server is rebuilding the Y.Doc in Redis.
    // Show "Repairing..." so users understand why activity is paused.
    // This clears automatically when messageDamaged arrives (handleDamagedReset → "Restoring...").
    provider.on('repairing', () => {
      console.log('[State] → REPAIRING (file-loader rebuilding Y.Doc from S3)')
      // Collaborating and repairing are mutually exclusive — clear the check hold
      // and cancel its safety timeout so both buttons don't appear simultaneously.
      if (collaboratingSafetyTimeoutRef.current) {
        clearTimeout(collaboratingSafetyTimeoutRef.current)
        collaboratingSafetyTimeoutRef.current = null
      }
      setIsCollaboratingRef.current(false)
      setIsRepairingRef.current(true)
      // Safety timeout: if repairing doesn't transition to damaged/OK within 30s, clear it
      if (repairingSafetyTimeoutRef.current) clearTimeout(repairingSafetyTimeoutRef.current)
      repairingSafetyTimeoutRef.current = setTimeout(() => {
        console.warn('[State] Repairing safety timeout — clearing after 30s')
        setIsRepairingRef.current(false)
        repairingSafetyTimeoutRef.current = null
      }, 30_000)
    })

    // Server confirmed CRDT corruption (phantom delete detected).
    // Clear IDB and reconnect — do NOT clear the Y.Doc (see handleDamagedReset for why).
    provider.on('damaged', () => {
      console.warn('[State] → DAMAGED_RESETTING (messageDamaged received — clearing IDB)')
      if (collaboratingSafetyTimeoutRef.current) {
        clearTimeout(collaboratingSafetyTimeoutRef.current)
        collaboratingSafetyTimeoutRef.current = null
      }
      if (repairingSafetyTimeoutRef.current) {
        clearTimeout(repairingSafetyTimeoutRef.current)
        repairingSafetyTimeoutRef.current = null
      }
      setIsCollaborating(false) // damaged reset takes over from here
      handleDamagedReset()
    })

    // Server confirmed the post-sync consistency check passed — release the collaboration hold
    // and start periodic hash polling for convergence.
    provider.on('consistencyCheckOK', () => {
      console.log('[State] → NORMAL (consistencyCheckOK — releasing collaboration hold)')
      if (collaboratingSafetyTimeoutRef.current) {
        clearTimeout(collaboratingSafetyTimeoutRef.current)
        collaboratingSafetyTimeoutRef.current = null
      }
      if (repairingSafetyTimeoutRef.current) {
        clearTimeout(repairingSafetyTimeoutRef.current)
        repairingSafetyTimeoutRef.current = null
      }
      setIsCollaborating(false)
      // If a selective patch preceded this check, repairing was set — clear it now
      setIsRepairingRef.current(false)

      // Start periodic hash polling to detect drift during the session.
      // If the server's Y.Doc hash diverges from ours (and no update is in progress),
      // trigger a repair so the UI converges to the source of truth.
      if (hashPollIntervalRef.current) clearInterval(hashPollIntervalRef.current)
      hashPollIntervalRef.current = setInterval(async () => {
        const currentProvider = providerRef.current
        if (!currentProvider || !currentProvider.wsconnected) return
        // Skip if update is in progress — hash will diverge temporarily
        if (inProcessUpdatesRef.current.size > 0) return
        // Skip if already in a repair/restore cycle or repairing
        if (isDamagedResettingRef.current || isRestoringRef.current || isRepairingRef.current) return

        try {
          const wsUrl = (currentProvider as any).url || ''
          // Extract just the origin (protocol + host) — the WS URL includes room path and query params
          const parsedUrl = new URL(wsUrl.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:'))
          const httpBase = parsedUrl.origin
          const currentRoom = roomNameRef.current
          if (!httpBase || !currentRoom) return

          const res = await fetch(`${httpBase}/room-hash/${currentRoom}`, {
            signal: AbortSignal.timeout(5000),
          })
          if (!res.ok) return
          const { hash: serverHash } = await res.json()
          const clientHash = await currentProvider.computeDataMapHash()

          if (clientHash !== serverHash) {
            console.warn(
              `[HashPoll] Drift detected — client=${clientHash.slice(0, 8)} server=${serverHash.slice(0, 8)}. Triggering repair.`
            )
            handleDamagedReset()
          }
        } catch {
          // Network error — skip this cycle
        }
      }, HASH_POLL_INTERVAL_MS)
    })

    // Note: Load time recording is handled in the 'synced' event handler below (line 309)
    // This 'sync' event handler is kept for logging only
    provider.on('sync', (isSynced: boolean) => {
      if (isSynced) {
        // If we were restoring after a damaged reset, clear the restoring state now that we're synced
        if (isRestoringRef.current) {
          console.log('[State] → SYNCED (useEffect sync handler: damaged reset complete)')
          isRestoringRef.current = false
          setIsRestoringRef.current(false)
          // Only show "update failed" if user had in-flight edits when damaged arrived.
          // Stale-IDB restores (no edits) complete silently — no failure to report.
          if (isDamagedResetRef.current) {
            isDamagedResetRef.current = false
            addPhantomDeleteFailureRef.current()
          }
          // Re-enable children now that we have a fresh, synced state from the server.
          setIsDamagedResettingRef.current(false)
          isDamagedResettingRef.current = false
        }

        // Only access the map if it exists in share to avoid Y.js warnings
        const dataMap = doc.share.has('data') ? doc.getMap('data') : null

        // Post-sync consistency check: compare data map hashes with the server.
        // The consistency check + protective patch detects and repairs any drift
        // (stale CRDT items, missing keys, value mismatches) — making the old
        // test_deletes canary probe unnecessary.
        if (dataMap && isFirstSyncedRef.current) {
          // Post-sync consistency check: after a brief settling delay, compare
          // data map hashes with the server. Until the check resolves,
          // set isCollaborating=true so the evaluation renders as read-only.
          // Guarded by hasRunConsistencyCheckRef to prevent double-execution in case
          // the early handler (registered before connect()) already fired.
          if (!hasRunConsistencyCheckRef.current) {
            hasRunConsistencyCheckRef.current = true
            setIsCollaborating(true)
            setTimeout(() => {
              // If another state took over (restoring/repairing), or WS is gone, release now.
              if (isRestoringRef.current || isDamagedResettingRef.current || !provider.wsconnected) {
                setIsCollaborating(false)
                return
              }
              // Skip the check if there are in-flight updates — the server's hash would diverge
              // from ours and trigger a false damaged reset. Just release the hold.
              if (inProcessUpdatesRef.current.size > 0) {
                console.log('[ConsistencyCheck] Skipping — in-flight updates present, releasing hold')
                setIsCollaborating(false)
                return
              }
              provider.sendConsistencyCheck?.()
              // Safety net: if the server never responds, release after 15s.
              if (collaboratingSafetyTimeoutRef.current) {
                clearTimeout(collaboratingSafetyTimeoutRef.current)
              }
              collaboratingSafetyTimeoutRef.current = setTimeout(() => {
                console.warn('[ConsistencyCheck] Safety timeout — server did not respond in 15s, releasing hold')
                setIsCollaboratingRef.current(false)
                collaboratingSafetyTimeoutRef.current = null
              }, 15_000)
            }, 3000)
          }
        }
        const hasData = dataMap ? dataMap.size > 0 : false
        console.debug('[WS] sync event', { dataMapSize: dataMap ? dataMap.size : 0, isSynced })

        // Early exit: If WebSocket has synced AND received data, but IndexedDB hasn't synced yet,
        // proceed anyway. This handles the case where WebSocket data arrives before IndexedDB finishes
        // syncing (common on Vercel where WebSocket is faster than IndexedDB).
        // This prevents blocking indefinitely when IndexedDB sync never fires due to WebSocket interference.
        if (hasData && !persistence.synced && !persistenceSyncedRef.current) {
          const connectionStatus = provider.getFullConnectionStatus?.()
          console.log('[State] → SYNCED (WS synced with data before IDB — opening Gate 2 early)', {
            dataMapSize: dataMap ? dataMap.size : 0,
          })
          persistenceSyncedRef.current = true
          setPersistenceSynced(true)
        }

      }
    })

    // Keep the old initialSyncComplete listener for backwards compatibility (in case it gets added later)
    provider.on('initialSyncComplete', () => {
      // This event doesn't seem to be emitted, but keeping for compatibility
      console.log(
        'initialSyncComplete event received (if this appears, the event is now working)'
      )
    })
    provider.on('eventMessage', (message: string) => {
      // Only access the map if it exists in share to avoid Y.js warnings
      const dataMap = doc.share.has('data') ? doc.getMap('data') : null
      console.log('Handling event message:', {
        message,
        docGuid: doc.guid,
        dataMapState: dataMap ? dataMap.toJSON() : {},
        timestamp: new Date().toISOString(),
      })
      // Use ref to get current function to avoid stale closures
      updateEventMessageRelatedStateRef.current(message)
    })

    provider.on('synced', (isSynced: boolean) => {
      // Only access the map if it exists in share to avoid Y.js warnings
      const dataMap = doc.share.has('data') ? doc.getMap('data') : null
      console.log('Provider synced:', {
        isSynced,
        docGuid: doc.guid,
        dataMapState: dataMap ? dataMap.toJSON() : {},
        dataMapSize: dataMap ? dataMap.size : 0,
        dataMapKeys: dataMap ? Array.from(dataMap.keys()) : [],
        persistenceState: persistence.synced,
        providerSynced: (provider as any).synced,
        docConnections: (doc as CustomDoc).conns.size,
        timestamp: new Date().toISOString(),
      })
      // Note: Empty data map is normal for new evaluations, so we don't warn
      if (isSynced && (!dataMap || dataMap.size === 0)) {
        console.log(
          'ℹ️ Provider synced - data map is empty (normal for new evaluations)'
        )
      }

      // Note: Evaluation create end recording is now handled in a separate useEffect
      // that watches for Slate content availability (see below). This ensures the
      // screen has actually been generated before recording the create end event.

      // Record evaluation load time on first successful sync (initial load)
      if (isSynced && isFirstSyncedRef.current) {
        isFirstSyncedRef.current = false // Mark that we've done the initial sync

        console.log('Initial sync complete (via synced event):', {
          docGuid: doc.guid,
          dataMapSize: dataMap ? dataMap.size : 0,
          persistenceState: persistence.synced,
          providerSynced: (provider as any).synced,
          timestamp: new Date().toISOString(),
        })

        // Record evaluation load time performance metric (only on initial load, not updates)
        // Use ref if available, otherwise fall back to prop (for backwards compatibility)
        const currentLoadStartTime = loadStartTimeRef?.current ?? loadStartTime
        console.log(
          '[EvaluationProvider] Checking conditions for load time recording:',
          {
            loadStartTime: currentLoadStartTime,
            loadStartTimeFromRef: loadStartTimeRef?.current,
            loadStartTimeFromProp: loadStartTime,
            evaluationId,
            hasRecordedLoadTimeRef: !!hasRecordedLoadTimeRef,
            hasRecordedLoadTimeRefCurrent: hasRecordedLoadTimeRef?.current,
            graphqlQueryTime,
          }
        )

        // Record evaluation load end event (when ydoc sync completes)
        const currentSessionId = loadSessionIdRef?.current ?? loadSessionId
        if (
          currentSessionId &&
          evaluationId &&
          hasRecordedLoadTimeRef &&
          !hasRecordedLoadTimeRef.current
        ) {
          console.log(
            `[Evaluation Load] 📊 Evaluation load complete: evaluation_id=${evaluationId}, session_id=${currentSessionId}`
          )

          // Fire-and-forget fetch to REST API route (zero rendering impact, no server action overhead)
          const controller = new AbortController()
          const endTimeoutId = setTimeout(() => controller.abort(), 5000)
          const totalLoadTime = currentLoadStartTime
            ? (performance.now() - currentLoadStartTime) / 1000
            : null

          fetch('/api/evaluation-load-end', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId: currentSessionId,
              evaluationId,
              frontendGraphqlTime: graphqlQueryTime || null,
              totalLoadTime,
            }),
            signal: controller.signal,
            keepalive: true,
          })
            .then((res) => res.json())
            .then((data) => {
              clearTimeout(endTimeoutId)
              if (data?.success) {
                console.log(
                  `[Evaluation Load] Evaluation load end recorded, duration=${data.duration?.toFixed(2)}s`
                )
              }
            })
            .catch(() => {
              clearTimeout(endTimeoutId)
            })

          // Mark as recorded to prevent duplicate calls on updates
          hasRecordedLoadTimeRef.current = true
        } else {
          console.warn(
            '[EvaluationProvider] Load end recording skipped - conditions not met:',
            {
              hasSessionId: !!currentSessionId,
              hasEvaluationId: !!evaluationId,
              hasRecordedLoadTimeRef: !!hasRecordedLoadTimeRef,
              alreadyRecorded: hasRecordedLoadTimeRef?.current,
            }
          )
        }
      }
    })

    provider.on('status', (statusArray: string[]) => {
      const status = statusArray?.[0] || ''
      // Only access the map if it exists in share to avoid Y.js warnings
      const dataMap = doc.share.has('data') ? doc.getMap('data') : null
      const connectionStatus = provider.getFullConnectionStatus?.()
      const wsConnected = connectionStatus?.wsConnected ?? false
      const isWebSocketOpen = provider.ws?.readyState === WebSocket.OPEN

      console.log('Provider status:', {
        status,
        docGuid: doc.guid,
        wsState: provider.ws?.readyState,
        wsConnected,
        isWebSocketOpen,
        dataMapState: dataMap ? dataMap.toJSON() : {},
        connectionStatus,
        timestamp: new Date().toISOString(),
      })

      // CRITICAL: If WebSocket connects and IndexedDB is already synced,
      // mark persistenceSynced as true (it may have been waiting for WebSocket connection)
      if (
        status === 'connected' &&
        isWebSocketOpen &&
        persistence.synced &&
        !persistenceSyncedRef.current
      ) {
        console.log(
          '✅ WebSocket connected and IndexedDB already synced - marking as synced:',
          {
            docGuid: doc.guid,
            persistenceSynced: persistence.synced,
            wsConnected,
            isWebSocketOpen,
            timestamp: new Date().toISOString(),
          }
        )
        persistenceSyncedRef.current = true
        setPersistenceSynced(true)
      }
    })

    doc.on('synced', (isSynced: boolean) => {
      // Only access the map if it exists in share to avoid Y.js warnings
      const dataMap = doc.share.has('data') ? doc.getMap('data') : null
      console.log('Doc synced:', {
        isSynced,
        docGuid: doc.guid,
        dataMapState: dataMap ? dataMap.toJSON() : {},
        persistenceState: persistence.synced,
        timestamp: new Date().toISOString(),
      })
    })
    doc.on('syncing', (isSyncing: boolean) => {
      // Only access the map if it exists in share to avoid Y.js warnings
      const dataMap = doc.share.has('data') ? doc.getMap('data') : null
      console.log('Doc syncing:', {
        isSyncing,
        docGuid: doc.guid,
        dataMapState: dataMap ? dataMap.toJSON() : {},
        persistenceState: persistence.synced,
        timestamp: new Date().toISOString(),
      })
    })
    doc.on('status', ({ status }: { status: string }) => {
      // Only access the map if it exists in share to avoid Y.js warnings
      const dataMap = doc.share.has('data') ? doc.getMap('data') : null
      console.log('Doc status:', {
        status,
        docGuid: doc.guid,
        dataMapState: dataMap ? dataMap.toJSON() : {},
        persistenceState: persistence.synced,
        timestamp: new Date().toISOString(),
      })
    })
    // Track local changes to verify they sync to remote
    const localChangeTracker = new Map<
      string,
      { timestamp: number; keys: string[] }
    >()

    doc.on('update', (update: Uint8Array, origin: any, doc: Y.Doc) => {
      // Only access the map if it exists in share to avoid Y.js warnings
      const dataMap = doc.share.has('data') ? doc.getMap('data') : null
      if (!dataMap) {
        // Map doesn't exist yet, skip processing
        return
      }

      // Determine update type
      const isLocal = origin !== null && origin !== undefined
      /**
       * salman/websocket-fixes
       * Improved check of origin to be more specific
       */
      const isIndexedDB = origin instanceof IndexeddbPersistence
      const isRemote = !isLocal
      const isLocalUserChange = isLocal && !isIndexedDB

      // Log IndexedDB updates to verify they're being detected
      if (isIndexedDB) {
        console.log('🔍 IndexedDB update detected:', {
          roomName: (doc as CustomDoc).roomName,
          updateSize: update.length,
          dataMapSize: dataMap.size,
          dataMapKeys: Array.from(dataMap.keys()).slice(0, 10),
          hasProvider: !!provider,
          wsConnected: provider?.wsconnected,
          wsState: provider?.ws?.readyState,
          docSynced: provider?.isDocSynced?.() ?? false,
          timestamp: new Date().toISOString(),
        })
      }

      const updateInfo = {
        roomName: (doc as CustomDoc).roomName,
        origin: isLocalUserChange
          ? 'local-user'
          : isIndexedDB
          ? 'indexeddb'
          : 'remote',
        originType: typeof origin,
        isIndexedDB: isIndexedDB,
        isLocalUserChange: isLocalUserChange,
        dataMapState: dataMap.toJSON(),
        updateSize: update.length,
        docGuid: doc.guid,
        timestamp: new Date().toISOString(),
        providerRoom: provider?.roomname,
        isConnected: provider?.wsconnected,
        wsState: provider?.ws?.readyState,
        stack: new Error().stack, // This will show us where the update originated
      }

      // Track local user changes to verify they sync to remote
      if (isLocalUserChange) {
        const updateId = `${Date.now()}-${Math.random()}`
        const changedKeys = Array.from(dataMap.keys())
        localChangeTracker.set(updateId, {
          timestamp: Date.now(),
          keys: changedKeys,
        })

        // Check if websocket is connected - if not, warn (but throttle to avoid spam)
        // Only warn if provider exists but is not connected (not if provider is undefined)
        if (provider) {
          if (!provider.wsconnected) {
            // Only warn if websocket has been initialized (past initial load)
            // This prevents spam during initial load when websocket isn't connected yet
            if (provider.ws?.readyState !== undefined) {
              // Throttle warnings - only warn once per 10 seconds
              const lastWarning =
                (localChangeTracker as any).lastDisconnectWarning || 0
              const now = Date.now()
              if (now - lastWarning > 10000) {
                console.warn(
                  '⚠️ LOCAL CHANGE MADE BUT WEBSOCKET NOT CONNECTED:',
                  {
                    updateId,
                    keys: changedKeys.slice(0, 10), // Only log first 10 keys to avoid spam
                    totalKeys: changedKeys.length,
                    wsConnected: provider.wsconnected,
                    wsState: provider.ws?.readyState,
                  }
                )
                ;(localChangeTracker as any).lastDisconnectWarning = now
              }
            }
          } else {
            // Only log if there are few keys to avoid spam
            if (changedKeys.length < 20) {
              console.log('✅ LOCAL CHANGE - should sync to remote:', {
                updateId,
                keys: changedKeys,
                wsConnected: provider.wsconnected,
              })
            }
          }
        } else {
          // Provider not initialized yet - this is normal during initial load
          // Don't warn, just silently track
        }

        // Clean up old tracked changes (older than 30 seconds)
        const now = Date.now()
        for (const [id, change] of localChangeTracker.entries()) {
          if (now - change.timestamp > 30000) {
            localChangeTracker.delete(id)
          }
        }
      }

      // When IndexedDB loads changes, ensure websocket is connected and synced
      // This ensures IndexedDB changes get sent to the server
      if (isIndexedDB && provider) {
        const changedKeys = Array.from(dataMap.keys())

        if (provider.wsconnected) {
          // Websocket is connected - ensure document is synced so changes get sent
          if (
            provider.ensureSynced &&
            typeof provider.ensureSynced === 'function'
          ) {
            // Check if doc is already synced - if not, ensureSynced will attempt to sync
            const isDocSynced = provider.isDocSynced?.() ?? false
            if (!isDocSynced) {
              console.log(
                '📤 IndexedDB change loaded - ensuring websocket sync:',
                {
                  keys: changedKeys.slice(0, 10),
                  totalKeys: changedKeys.length,
                  wsConnected: provider.wsconnected,
                  wsState: provider.ws?.readyState,
                  docSynced: isDocSynced,
                }
              )
              provider.ensureSynced()
            } else {
              // Doc is synced, changes should automatically be sent
              if (changedKeys.length < 20) {
                console.log(
                  '✅ IndexedDB change loaded - doc synced, should send to server:',
                  {
                    keys: changedKeys,
                    wsConnected: provider.wsconnected,
                  }
                )
              }
            }
          }
        } else {
          // Websocket not connected - warn that changes won't sync until it connects
          console.warn(
            '⚠️ IndexedDB change loaded but websocket not connected - will sync when websocket connects:',
            {
              keys: changedKeys.slice(0, 10),
              totalKeys: changedKeys.length,
              wsConnected: provider.wsconnected,
              wsState: provider.ws?.readyState,
              note: 'Change is saved locally and will sync when websocket connects',
            }
          )
        }
      }

      // If this is a remote update, check if it matches a local change we tracked
      if (isRemote) {
        // This means the change came from the server - verify it matches our local changes
        const changedKeys = Array.from(dataMap.keys())
        // Only log if there are few keys to avoid spam
        if (changedKeys.length < 20) {
          console.log('📥 REMOTE UPDATE RECEIVED:', {
            keys: changedKeys,
            trackedLocalChanges: localChangeTracker.size,
          })
        }
      }

      // Only log update info for local user changes or if there are issues
      // This reduces console spam
      if (isLocalUserChange || !provider?.wsconnected) {
        console.log('Y.Doc update:', updateInfo)
      }

      // Log the actual update content
      try {
        const updateContent = Y.decodeUpdate(update)
        console.log('Update content:', {
          roomName: (doc as CustomDoc).roomName,
          updateContent,
        })
      } catch (e) {
        console.log('Could not decode update content')
      }
    })

    doc.on('sync', (isSynced: boolean) => {
      // Only access the map if it exists in share to avoid Y.js warnings
      const dataMap = doc.share.has('data') ? doc.getMap('data') : null
      console.log('Y.Doc sync state changed:', {
        roomName: (doc as CustomDoc).roomName,
        isSynced,
        dataMapState: dataMap ? dataMap.toJSON() : {},
        persistenceState: persistence.synced,
        providerRoom: provider?.roomname,
      })
    })

    return () => {
      if (provider) {
        // Clear persistence check interval
        const persistenceCheckInterval = (provider as any)
          ._persistenceCheckInterval
        if (persistenceCheckInterval) {
          clearInterval(persistenceCheckInterval)
        }

        // Clear persistence synced check timeout
        const syncedCheckTimeout = (provider as any)
          ._persistenceSyncedCheckTimeout
        if (syncedCheckTimeout) {
          clearTimeout(syncedCheckTimeout)
        }

        // Clear persistence fallback timeout
        const fallbackTimeout = (provider as any)._persistenceFallbackTimeout
        if (fallbackTimeout) {
          clearTimeout(fallbackTimeout)
        }

        /**
         * salman/websocket-fixes
         * ProviderManager.resetProvider is already disconnecting
         * and destroying the provider below so need to do this explicity
         */
        // newProvider.disconnect()
        // newProvider.destroy()

        // CRITICAL: Only reset the provider if it's still the same one we created
        // This prevents resetting a new provider that was created after this cleanup started
        const currentProvider = ProviderManager.providers.get(roomName)
        if (currentProvider === provider) {
          // Only reset if it's the same provider instance we created
          ProviderManager.resetProvider(roomName)
        } else {
          console.warn(
            `[WebSocket Cleanup] Skipping provider reset - provider changed (new provider was created):`,
            {
              roomName,
              oldProviderGuid: provider.doc?.guid,
              newProviderGuid: currentProvider?.doc?.guid,
              note: 'New provider will handle its own cleanup',
            }
          )
        }

        console.log(
          `%c[WebSocket Cleanup] Cleaned up provider and bindings for room: ${roomName}`,
          'color: #ff9900; font-weight: bold;'
        )
      }

      // Clear hash polling interval
      if (hashPollIntervalRef.current) {
        clearInterval(hashPollIntervalRef.current)
        hashPollIntervalRef.current = null
      }

      // Clear collaborating/repairing state and safety timeouts so atoms don't stay
      // true when navigating to a different evaluation or on unmount.
      if (collaboratingSafetyTimeoutRef.current) {
        clearTimeout(collaboratingSafetyTimeoutRef.current)
        collaboratingSafetyTimeoutRef.current = null
      }
      if (repairingSafetyTimeoutRef.current) {
        clearTimeout(repairingSafetyTimeoutRef.current)
        repairingSafetyTimeoutRef.current = null
      }
      setIsCollaboratingRef.current(false)
      setIsRepairingRef.current(false)

      // Reset all Jotai event atoms on unmount
      resetEventAtomsRef.current()

      // Finally reset the store
      resetEvaluationStore()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    persistenceInitialized,
    provider,
    doc,
    roomName,
    // memberId,
    // evaluationId,
    // graphqlQueryTime,
    // handleHardReset,
    // Refs don't need to be in dependency array - they're stable references
    // hasRecordedLoadTimeRef,
    // loadSessionId,
    // loadSessionIdRef,
    // loadStartTime,
    // loadStartTimeRef,
    // updateEventMessageRelatedState - using ref to avoid re-runs
    // hasRecordedCreateEndRef,
    // isFirstSyncedRef,
  ])

  // Watch for Slate content availability to trigger create end event
  // This ensures the screen has actually been generated before recording the create end event
  // IMPORTANT: Only subscribe after IndexedDB has synced to avoid interfering with the sync process
  useEffect(() => {
    if (!provider || !evaluationId || typeof window === 'undefined') {
      return
    }

    const createSessionId = sessionStorage.getItem(
      'evaluation-create-session-id'
    )
    if (!createSessionId || hasRecordedCreateEndRef.current) {
      return
    }

    // Check if IndexedDB is synced - only subscribe after it's synced
    // This prevents the subscription from interfering with IndexedDB sync during create
    const persistence = (provider as any)._persistence
    if (persistence && !persistenceSynced) {
      // IndexedDB not synced yet - don't subscribe yet to avoid interfering with sync
      // The effect will re-run when persistenceSynced becomes true
      return
    }

    // Subscribe to evaluationStore.dataTablesSlate changes
    // The screen is ready when dataTablesSlate.children has content
    const unsubscribe = subscribe(evaluationStore.dataTablesSlate, () => {
      const hasSlateContent =
        evaluationStore.dataTablesSlate.children.length > 0
      const dataMap = provider.doc.share.has('data')
        ? provider.doc.getMap('data')
        : null
      const hasData = dataMap ? dataMap.size > 0 : false
      const isProviderSynced = provider.isDocSynced?.() ?? false

      // Evaluation is ready when:
      // 1. Provider is synced
      // 2. Data map has data
      // 3. Slate content is available (screen has been generated)
      const isReady = isProviderSynced && hasData && hasSlateContent

      if (isReady && !hasRecordedCreateEndRef.current) {
        console.warn(
          `[Evaluation Create] ⚠️ 📊 Evaluation create complete (Slate rendered): evaluation_id=${evaluationId}, session_id=${createSessionId}`
        )

        // Record create end event (backend will use run_first_update completion time if later)
        // Fire-and-forget with timeout to prevent hanging on Vercel
        import('~/app/(dashboard)/performance-metrics')
          .then(({ recordEvaluationCreateEnd }) => {
            // Use Promise.race with timeout to prevent hanging
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Timeout after 5s')), 5000)
            )

            Promise.race([
              recordEvaluationCreateEnd({
                sessionId: createSessionId,
                evaluationId,
              }),
              timeoutPromise,
            ])
              .then((result: any) => {
                const data = result?.recordEvaluationCreateEnd
                if (data?.success) {
                  console.warn(
                    `[Evaluation Create] ⚠️ ✅ Evaluation create end recorded successfully, duration=${data.duration?.toFixed(
                      2
                    )}s`
                  )
                  // Clean up sessionStorage
                  sessionStorage.removeItem('evaluation-create-session-id')
                  hasRecordedCreateEndRef.current = true
                  // Unsubscribe since we've recorded the event
                  unsubscribe()
                  // Trigger a complete page reload to ensure clean state
                  // Use a small delay to ensure the event is fully processed
                  setTimeout(() => {
                    console.warn(
                      '[Evaluation Create] 🔄 Reloading page after create end'
                    )
                    // window.location.reload()
                  }, 100)
                } else {
                  console.warn(
                    '[Evaluation Create] ⚠️ Failed to record end event (non-critical):',
                    {
                      message: data?.message,
                      fullResponse: result,
                      sessionId: createSessionId,
                      evaluationId,
                    }
                  )
                }
              })
              .catch((error) => {
                console.error(
                  '[Evaluation Create] ❌ Error recording end event (non-critical):',
                  {
                    error,
                    errorMessage: error?.message,
                    errorStack: error?.stack,
                    sessionId: createSessionId,
                    evaluationId,
                  }
                )
                // Still clean up and unsubscribe even if recording failed
                sessionStorage.removeItem('evaluation-create-session-id')
                hasRecordedCreateEndRef.current = true
                unsubscribe()
              })
          })
          .catch((error) => {
            console.error(
              '[Evaluation Create] ❌ Error importing performance-metrics (non-critical):',
              error
            )
          })
      }
    })

    // Also check immediately in case Slate content is already available
    const hasSlateContent = evaluationStore.dataTablesSlate.children.length > 0
    const dataMap = provider.doc.share.has('data')
      ? provider.doc.getMap('data')
      : null
    const hasData = dataMap ? dataMap.size > 0 : false
    const isProviderSynced = provider.isDocSynced?.() ?? false
    const isReady = isProviderSynced && hasData && hasSlateContent

    if (isReady && !hasRecordedCreateEndRef.current) {
      console.warn(
        `[Evaluation Create] ⚠️ 📊 Evaluation create complete (Slate already rendered): evaluation_id=${evaluationId}, session_id=${createSessionId}`
      )

      import('~/app/(dashboard)/performance-metrics')
        .then(({ recordEvaluationCreateEnd }) => {
          // Use Promise.race with timeout to prevent hanging
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Timeout after 5s')), 5000)
          )

          Promise.race([
            recordEvaluationCreateEnd({
              sessionId: createSessionId,
              evaluationId,
            }),
            timeoutPromise,
          ])
            .then((result: any) => {
              const data = result?.recordEvaluationCreateEnd
              if (data?.success) {
                console.warn(
                  `[Evaluation Create] ⚠️ ✅ Evaluation create end recorded successfully, duration=${data.duration?.toFixed(
                    2
                  )}s`
                )
                sessionStorage.removeItem('evaluation-create-session-id')
                hasRecordedCreateEndRef.current = true
                unsubscribe()
              } else {
                console.warn(
                  '[Evaluation Create] ⚠️ Failed to record end event (non-critical):',
                  {
                    message: data?.message,
                    fullResponse: result,
                    sessionId: createSessionId,
                    evaluationId,
                  }
                )
              }
            })
            .catch((error) => {
              console.error(
                '[Evaluation Create] ❌ Error recording end event (non-critical):',
                {
                  error,
                  errorMessage: error?.message,
                  errorStack: error?.stack,
                  sessionId: createSessionId,
                  evaluationId,
                }
              )
              // Still clean up and unsubscribe even if recording failed
              sessionStorage.removeItem('evaluation-create-session-id')
              hasRecordedCreateEndRef.current = true
              unsubscribe()
            })
        })
        .catch((error) => {
          console.error(
            '[Evaluation Create] ❌ Error importing performance-metrics (non-critical):',
            error
          )
        })
    }

    return () => {
      unsubscribe()
    }
  }, [provider, evaluationId, persistenceSynced])

  // Watch for user-initiated recover requests (from the menu)
  // Instead of calling handleDamagedReset directly, send a recover request to the server.
  // The server triggers file-loader repair (rebuilds Y.Doc from DB/S3), then sends
  // messageDamaged which triggers handleDamagedReset automatically with fresh server data.
  const recoverRequested = useRecoverRequested()
  const recoverRequestedRef = useRef(recoverRequested)
  useEffect(() => {
    if (recoverRequested > recoverRequestedRef.current) {
      recoverRequestedRef.current = recoverRequested
      const currentProvider = providerRef.current
      if (currentProvider?.ws && currentProvider.ws.readyState === WebSocket.OPEN) {
        sendRecoverRequest(currentProvider.ws)
      } else {
        // WebSocket not connected — fall back to direct damaged reset
        console.warn('[Recover] WebSocket not connected — falling back to direct reset')
        handleDamagedReset()
      }
    }
  }, [recoverRequested, handleDamagedReset])

  // Memoize context value to prevent unnecessary re-renders
  // Must be called before any early returns to follow Rules of Hooks
  const contextValue = useMemo(
    () => (provider ? { provider, doc: provider.doc } : undefined),
    [provider]
  )

  // Show loading state instead of null to prevent white screen
  // The provider should be set shortly after mount
  if (!provider) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-white">
        <div className="text-gray-500">Loading evaluation...</div>
      </div>
    )
  }

  return (
    <EvaluationContext.Provider value={contextValue as EvaluationContextState}>
      {/* Unmount children during damaged reset to prevent Slate removeChild race conditions.
          The Slate editor tree must be fully unmounted before resetEvaluationStore() fires.
          Children are re-mounted after the provider re-syncs with the server (sync event). */}
      {isDamagedResetting ? (
        <div className="flex items-center justify-center h-screen w-full bg-white">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-500" />
            <p className="text-sm text-gray-500">Syncing evaluation&hellip;</p>
          </div>
        </div>
      ) : children}
    </EvaluationContext.Provider>
  )
}

export function useEvaluationContext() {
  const ctx = useContext(EvaluationContext)
  if (typeof ctx === 'undefined') {
    throw new Error(
      'Cannot call useEvaluationContext outside of EvaluationProvider'
    )
  }
  return ctx
}

export function useEvaluationKV() {
  const { doc } = useEvaluationContext()
  // getMap will create the map if it doesn't exist, which is what we want
  const yMap = useMemo(() => doc.getMap<string>('data'), [doc])
  useEffect(() => {
    bindEvaluationStoreObjectToYObject(evaluationStore.kv, yMap)
  }, [yMap])
  return useSnapshot(evaluationStore.kv)
}

export function useEvaluationQuestionnaireKV() {
  const { doc } = useEvaluationContext()
  const yMap = useMemo(() => doc.getMap<string>('questionnaire_binds'), [doc])
  useEffect(() => {
    bindEvaluationStoreObjectToYObject(evaluationStore.questionnaire.kv, yMap)
  }, [yMap])
  return useSnapshot(evaluationStore.questionnaire.kv)
}

export function useEvaluationQuestionnaireQuestions() {
  const { doc } = useEvaluationContext()
  const yMap = useMemo(
    () => doc.getMap<Y.XmlText[]>('questionnaire_questions'),
    [doc]
  )
  useEffect(() => {
    bindEvaluationStoreObjectToYObject(
      evaluationStore.questionnaire.questions,
      yMap
    )
  }, [yMap])
  return useSnapshot(evaluationStore.questionnaire.questions)
}

export function useEvaluationChangeLogs() {
  const { doc } = useEvaluationContext()

  useEffect(() => {
    if (doc) {
      bindChangeLogsToYDoc(doc)
    }
  }, [doc])

  return useSnapshot(evaluationStore.changeLogs)
}

export function useEvaluationDataTablesSlate() {
  const { doc } = useEvaluationContext()

  useEffect(() => {
    if (doc) {
      bindDataTablesSlateToYDoc(doc)
    }
  }, [doc])

  return useSnapshot(evaluationStore.dataTablesSlate)
}

export function useEvaluationQuestionnaireQuestionIds() {
  const { doc } = useEvaluationContext()
  const yMap = useMemo(
    () => doc.getMap<string[]>('questionnaire_question_ids'),
    [doc]
  )
  useEffect(() => {
    bindEvaluationStoreObjectToYObject(
      evaluationStore.questionnaire.questionsIds,
      yMap
    )
  }, [yMap])
  return useSnapshot(evaluationStore.questionnaire.questionsIds)
}

function useTableKey(tableType: string, tableName: string) {
  const { doc } = useEvaluationContext()
  const [tableKey, setTableKey] = useState<string | null>(
    doc.share.has(tableType)
      ? tableType
      : doc.share.has(tableName)
      ? tableName
      : null
  )
  const handlerRef = useRef<
    ((update: Uint8Array, origin: any, doc: Y.Doc) => void) | null
  >(null)
  const isHandlerRegistered = useRef(false)

  useEffect(() => {
    if (tableKey) {
      return
    }
    if (!doc) {
      return
    }

    // Clean up any existing handler before creating a new one
    if (isHandlerRegistered.current && handlerRef.current) {
      doc.off('update', handlerRef.current)
      isHandlerRegistered.current = false
      handlerRef.current = null
    }

    const updateHandler = (_update: Uint8Array, _origin: any, doc: Y.Doc) => {
      let mapKey: string | null = null
      // Try finding the map by type first
      if (doc.share.has(tableType)) {
        mapKey = tableType
      }
      // Try finding the map by name for legacy reports
      if (doc.share.has(tableName)) {
        mapKey = tableName
      }
      if (mapKey) {
        // Remove handler if it was registered
        if (isHandlerRegistered.current && handlerRef.current) {
          doc.off('update', handlerRef.current)
          isHandlerRegistered.current = false
          handlerRef.current = null
        }
      }
      setTableKey(mapKey)
    }

    // Store handler reference
    handlerRef.current = updateHandler

    // Check immediately
    updateHandler(new Uint8Array(), null, doc)

    // Only register if we haven't found the key yet
    if (!tableKey && !isHandlerRegistered.current) {
      doc.on('update', updateHandler)
      isHandlerRegistered.current = true
    }

    return () => {
      // Only remove handler if it was actually registered
      if (isHandlerRegistered.current && handlerRef.current) {
        doc.off('update', handlerRef.current)
        isHandlerRegistered.current = false
        handlerRef.current = null
      }
    }
  }, [doc, tableType, tableName, tableKey])

  return tableKey
}

/**
 * This hook is used to check if the basic evaluation content is loaded in the doc.
 * @returns boolean - true if the evaluation content is loaded, false otherwise
 */
export function useBasicEvaluationContentCheck() {
  const { doc } = useEvaluationContext()
  const [isReady, setIsReady] = useState(false)

  const checkContentReady = useCallback((doc: Y.Doc) => {
    const sharedType = doc.get('content', Y.XmlText) as Y.XmlText
    const content = safeYTextToSlateElement(sharedType)
    if (content.children.length > 1) {
      return true
    }
    return false
  }, [])

  useEffect(() => {
    if (!doc || isReady) return

    const isContentReady = checkContentReady(doc)
    if (isContentReady) {
      setIsReady(true)
      return
    }

    const updateHandler = () => {
      if (isReady) return

      const isContentReady = checkContentReady(doc)
      if (isContentReady) {
        setIsReady(true)
        doc.off('update', updateHandler)
      }
    }

    doc.on('update', updateHandler)
    return () => {
      doc.off('update', updateHandler)
    }
  }, [doc, isReady])

  return isReady
}

export function useIsEvaluationReady() {
  const { doc, provider } = useEvaluationContext()
  const [isReady, setIsReady] = useState(false)
  const updateHandlerRef = useRef<
    ((update: Uint8Array, origin: any, doc: Y.Doc) => void) | null
  >(null)
  const syncHandlerRef = useRef<((isSynced: boolean) => void) | null>(null)
  const dataMapObserverRef = useRef<(() => void) | null>(null)
  const slateUnsubscribeRef = useRef<(() => void) | null>(null)
  const isUpdateHandlerRegistered = useRef(false)
  const isSyncHandlerRegistered = useRef(false)
  const isDataMapObserverRegistered = useRef(false)
  const isSlateSubscriptionRegistered = useRef(false)
  const lastDocGuidRef = useRef<string | null>(null)
  const lastProviderRoomRef = useRef<string | null>(null)
  const docRef = useRef<Y.Doc | null>(null)
  const providerRef = useRef<CustomWebsocketProvider | null>(null)

  // Use useSnapshot to reactively track Slate content changes
  // This is more reliable than subscribe for nested array changes
  const slateSnapshot = useSnapshot(evaluationStore.dataTablesSlate)

  // Use stable identifiers instead of objects to prevent unnecessary re-runs
  const docGuid = doc?.guid ?? null
  const providerRoom = provider?.roomname ?? null

  // Store current doc/provider in refs so handlers always have latest values
  docRef.current = doc
  providerRef.current = provider

  // Separate useEffect to react to Slate content changes via useSnapshot
  // This ensures we detect when Slate content arrives, even if the Y.Doc update handler doesn't fire
  // Must be at top level, not inside another useEffect
  useEffect(() => {
    const currentDoc = docRef.current
    if (!currentDoc) return

    const dataMapNow = currentDoc.share.has('data')
      ? currentDoc.getMap('data')
      : null
    const hasDataNow = dataMapNow ? dataMapNow.size > 0 : false
    const hasSlateContentNow = slateSnapshot.children.length > 0
    const currentProvider = providerRef.current
    const isProviderSyncedNow = currentProvider
      ? currentProvider.isDocSynced?.() ?? false
      : false
    const readyNow =
      (hasDataNow || hasSlateContentNow) &&
      (currentProvider ? isProviderSyncedNow : false)

    console.log('[useIsEvaluationReady] Slate snapshot effect fired', {
      hasDataNow,
      hasSlateContentNow,
      slateChildrenCount: slateSnapshot.children.length,
      isProviderSyncedNow,
      readyNow,
      timestamp: new Date().toISOString(),
    })

    setIsReady((currentReady) => {
      if (readyNow !== currentReady) {
        console.log(
          `[useIsEvaluationReady] Slate snapshot: setting ready to ${readyNow} (was ${currentReady})`
        )
        return readyNow
      }
      return currentReady
    })
  }, [slateSnapshot, docGuid, providerRoom]) // Re-run when Slate snapshot changes or doc/provider changes

  useEffect(() => {
    console.log('[useIsEvaluationReady] Effect running', {
      hasDoc: !!doc,
      hasProvider: !!provider,
      docGuid,
      providerRoom,
      timestamp: new Date().toISOString(),
    })

    if (!doc) {
      console.log('[useIsEvaluationReady] No doc, setting ready to false')
      setIsReady(false)
      lastDocGuidRef.current = null
      lastProviderRoomRef.current = null
      return
    }

    // Only set up handlers if doc/provider actually changed (by guid/roomname)
    const docChanged = docGuid !== lastDocGuidRef.current
    const providerChanged = providerRoom !== lastProviderRoomRef.current

    // If nothing changed and handlers are already registered, skip entirely
    // The event handlers will update state when changes occur
    if (!docChanged && !providerChanged && isUpdateHandlerRegistered.current) {
      // Doc and provider haven't changed and handlers are already registered
      // Don't do anything - let the event handlers manage state updates
      console.log(
        '[useIsEvaluationReady] Doc and provider unchanged, handlers registered, skipping'
      )
      return
    }

    // Clean up previous handlers if doc/provider changed
    if (
      docChanged &&
      isUpdateHandlerRegistered.current &&
      updateHandlerRef.current
    ) {
      // Get the old doc if it still exists, otherwise skip cleanup
      if (lastDocGuidRef.current && doc) {
        try {
          doc.off('update', updateHandlerRef.current)
        } catch (e) {
          // Doc might be destroyed, ignore
        }
      }
      isUpdateHandlerRegistered.current = false
      updateHandlerRef.current = null
    }
    // Clean up data map observer if doc changed
    if (
      docChanged &&
      isDataMapObserverRegistered.current &&
      dataMapObserverRef.current
    ) {
      const oldDoc = docRef.current
      if (oldDoc && oldDoc.share.has('data')) {
        try {
          const oldDataMap = oldDoc.getMap('data')
          oldDataMap.unobserve(dataMapObserverRef.current)
        } catch (e) {
          // DataMap might be destroyed, ignore
        }
      }
      isDataMapObserverRegistered.current = false
      dataMapObserverRef.current = null
    }
    if (
      providerChanged &&
      provider &&
      isSyncHandlerRegistered.current &&
      syncHandlerRef.current
    ) {
      try {
        provider.off('sync', syncHandlerRef.current)
      } catch (e) {
        // Provider might be destroyed, ignore
      }
      isSyncHandlerRegistered.current = false
      syncHandlerRef.current = null
    }

    // Update refs to track current doc/provider
    lastDocGuidRef.current = docGuid
    lastProviderRoomRef.current = providerRoom

    // Check if document has data
    // Only access the map if it exists in share to avoid Y.js warnings
    const dataMap = doc.share.has('data') ? doc.getMap('data') : null
    const hasData = dataMap ? dataMap.size > 0 : false
    const dataMapSize = dataMap ? dataMap.size : 0
    const dataMapKeys = dataMap ? Array.from(dataMap.keys()) : []

    // Also check for Slate content (dataTablesSlate) - this is the actual report content
    const hasSlateContent = evaluationStore.dataTablesSlate.children.length > 0
    const slateChildrenCount = evaluationStore.dataTablesSlate.children.length

    // Check if provider is synced (if provider exists)
    // If provider exists, it MUST be synced for evaluation to be ready
    // If provider doesn't exist yet, we can't be ready
    const isProviderSynced = provider
      ? provider.isDocSynced?.() ?? false
      : false
    const providerSyncedValue = provider ? (provider as any).synced : null
    const persistenceSynced = provider
      ? (provider as any)._persistence?.synced ?? false
      : null
    const customDoc = doc as CustomDoc
    const hasConnections = customDoc.conns.size > 0

    console.log('[useIsEvaluationReady] State check:', {
      hasData,
      dataMapSize,
      dataMapKeys: dataMapKeys.slice(0, 10), // First 10 keys
      hasSlateContent,
      slateChildrenCount,
      hasProvider: !!provider,
      isProviderSynced,
      providerSyncedValue,
      persistenceSynced,
      hasConnections,
      docConnections: customDoc.conns.size,
      timestamp: new Date().toISOString(),
    })

    // Evaluation is ready if:
    // 1. Document has data (data map OR Slate content), AND
    // 2. Provider exists AND is synced (we need provider to be synced before making GraphQL calls)
    // Note: We check Slate content because that's what actually renders the report
    const ready =
      (hasData || hasSlateContent) && (provider ? isProviderSynced : false)

    // Set up listener to check when data becomes available
    // Use refs to access current doc/provider to avoid stale closures
    const updateHandler = (_update: Uint8Array, _origin: any, doc: Y.Doc) => {
      // Only access the map if it exists in share to avoid Y.js warnings
      const dataMapNow = doc.share.has('data') ? doc.getMap('data') : null
      const hasDataNow = dataMapNow ? dataMapNow.size > 0 : false
      // Also check Slate content
      const hasSlateContentNow =
        evaluationStore.dataTablesSlate.children.length > 0
      // Get current provider from ref to avoid stale closures
      const currentProvider = providerRef.current
      const isProviderSyncedNow = currentProvider
        ? currentProvider.isDocSynced?.() ?? false
        : false
      const readyNow =
        (hasDataNow || hasSlateContentNow) &&
        (currentProvider ? isProviderSyncedNow : false)

      // Log every update to see if handler is firing when data arrives
      console.log('[useIsEvaluationReady] Update handler fired', {
        hasDataNow,
        dataMapSize: dataMapNow ? dataMapNow.size : 0,
        hasSlateContentNow,
        slateChildrenCount: evaluationStore.dataTablesSlate.children.length,
        isProviderSyncedNow,
        readyNow,
        timestamp: new Date().toISOString(),
      })

      setIsReady((currentReady) => {
        if (readyNow !== currentReady) {
          console.log(
            '[useIsEvaluationReady] Update handler: ready state changed',
            {
              readyNow,
              wasReady: currentReady,
              hasDataNow,
              hasSlateContentNow,
              slateChildrenCount:
                evaluationStore.dataTablesSlate.children.length,
              isProviderSyncedNow,
              dataMapSize: dataMapNow ? dataMapNow.size : 0,
              timestamp: new Date().toISOString(),
            }
          )
          return readyNow
        }
        return currentReady
      })
    }

    // Store handler reference
    updateHandlerRef.current = updateHandler

    // Register update handler
    if (!isUpdateHandlerRegistered.current) {
      console.log('[useIsEvaluationReady] Registering update handler')
      doc.on('update', updateHandlerRef.current)
      isUpdateHandlerRegistered.current = true
    }

    // Set initial state directly - don't call updateHandler to avoid triggering re-renders
    // The event handlers will update state when changes occur
    setIsReady((currentIsReady) => {
      if (ready !== currentIsReady) {
        console.log(
          `[useIsEvaluationReady] Setting ready: ${ready} (was ${currentIsReady})`,
          {
            ready,
            wasReady: currentIsReady,
            reason:
              !hasData && !hasSlateContent
                ? 'no data or slate content'
                : !provider
                ? 'no provider'
                : !isProviderSynced
                ? 'provider not synced'
                : 'ready',
          }
        )
        return ready
      }
      return currentIsReady
    })

    // Also listen for provider sync events
    if (provider) {
      const syncHandler = (isSynced: boolean) => {
        // Get current doc from ref to avoid stale closures
        const currentDoc = docRef.current
        if (!currentDoc) return

        // Only access the map if it exists in share to avoid Y.js warnings
        const dataMapNow = currentDoc.share.has('data')
          ? currentDoc.getMap('data')
          : null
        const hasDataNow = dataMapNow ? dataMapNow.size > 0 : false
        const hasSlateContentNow =
          evaluationStore.dataTablesSlate.children.length > 0
        const currentProvider = providerRef.current
        console.log('[useIsEvaluationReady] Sync handler fired', {
          isSynced,
          hasData: hasDataNow,
          dataMapSize: dataMapNow ? dataMapNow.size : 0,
          hasSlateContent: hasSlateContentNow,
          slateChildrenCount: evaluationStore.dataTablesSlate.children.length,
          providerSynced: currentProvider
            ? (currentProvider as any).synced
            : null,
          persistenceSynced: currentProvider
            ? (currentProvider as any)._persistence?.synced
            : null,
          timestamp: new Date().toISOString(),
        })
        const readyNow = (hasDataNow || hasSlateContentNow) && isSynced
        setIsReady((currentReady) => {
          if (readyNow !== currentReady) {
            console.log(
              `[useIsEvaluationReady] Sync handler: setting ready to ${readyNow} (was ${currentReady})`
            )
            return readyNow
          }
          return currentReady
        })
      }
      syncHandlerRef.current = syncHandler

      // Register sync handler only if not already registered
      if (!isSyncHandlerRegistered.current) {
        console.log('[useIsEvaluationReady] Registering sync handler')
        provider.on('sync', syncHandler)
        isSyncHandlerRegistered.current = true
      }

      // IMPORTANT: Check if provider is already synced when we register the handler
      // This handles the case where sync happened before we registered the handler
      // Note: The initial state is already set above, so we don't need to check again here
      // The sync handler will fire if sync state changes
    } else {
      console.log(
        '[useIsEvaluationReady] No provider, skipping sync handler registration'
      )
    }

    // Subscribe to evaluationStore.dataTablesSlate.children changes to detect when Slate content is available
    // This is critical because the report content comes through Slate, not the data map
    // We subscribe to the children array specifically to ensure we catch changes
    if (!isSlateSubscriptionRegistered.current) {
      const slateUnsubscribe = subscribe(
        evaluationStore.dataTablesSlate,
        () => {
          const docForSlate = docRef.current
          if (!docForSlate) return

          const dataMapNow = docForSlate.share.has('data')
            ? docForSlate.getMap('data')
            : null
          const hasDataNow = dataMapNow ? dataMapNow.size > 0 : false
          const hasSlateContentNow =
            evaluationStore.dataTablesSlate.children.length > 0
          const currentProvider = providerRef.current
          const isProviderSyncedNow = currentProvider
            ? currentProvider.isDocSynced?.() ?? false
            : false
          const readyNow =
            (hasDataNow || hasSlateContentNow) &&
            (currentProvider ? isProviderSyncedNow : false)

          console.log(
            '[useIsEvaluationReady] Slate content subscription fired',
            {
              hasDataNow,
              hasSlateContentNow,
              slateChildrenCount:
                evaluationStore.dataTablesSlate.children.length,
              isProviderSyncedNow,
              readyNow,
              timestamp: new Date().toISOString(),
            }
          )

          setIsReady((currentReady) => {
            if (readyNow !== currentReady) {
              console.log(
                `[useIsEvaluationReady] Slate subscription: setting ready to ${readyNow} (was ${currentReady})`
              )
              return readyNow
            }
            return currentReady
          })
        }
      )
      slateUnsubscribeRef.current = slateUnsubscribe
      isSlateSubscriptionRegistered.current = true
      console.log(
        '[useIsEvaluationReady] Registered Slate content subscription',
        {
          currentSlateChildrenCount:
            evaluationStore.dataTablesSlate.children.length,
          timestamp: new Date().toISOString(),
        }
      )
    }

    // Also observe the data map directly to catch when it gets populated
    // This is important for creation flow where data arrives after initial sync
    // Get or create the data map - getMap will create it if it doesn't exist
    if (!isDataMapObserverRegistered.current) {
      try {
        // getMap will create the map if it doesn't exist, so we can always observe it
        const dataMapToObserve = doc.getMap('data')

        const dataMapObserver = () => {
          const docForObserver = docRef.current
          if (!docForObserver) return

          const dataMapNow = docForObserver.share.has('data')
            ? docForObserver.getMap('data')
            : null
          const hasDataNow = dataMapNow ? dataMapNow.size > 0 : false
          const hasSlateContentNow =
            evaluationStore.dataTablesSlate.children.length > 0
          const currentProvider = providerRef.current
          const isProviderSyncedNow = currentProvider
            ? currentProvider.isDocSynced?.() ?? false
            : false
          const readyNow =
            (hasDataNow || hasSlateContentNow) &&
            (currentProvider ? isProviderSyncedNow : false)

          console.log('[useIsEvaluationReady] Data map observer fired', {
            hasDataNow,
            dataMapSize: dataMapNow ? dataMapNow.size : 0,
            hasSlateContentNow,
            slateChildrenCount: evaluationStore.dataTablesSlate.children.length,
            isProviderSyncedNow,
            readyNow,
            timestamp: new Date().toISOString(),
          })

          setIsReady((currentReady) => {
            if (readyNow !== currentReady) {
              console.log(
                `[useIsEvaluationReady] Data map observer: setting ready to ${readyNow} (was ${currentReady})`
              )
              return readyNow
            }
            return currentReady
          })
        }

        dataMapObserverRef.current = dataMapObserver
        dataMapToObserve.observe(dataMapObserver)
        isDataMapObserverRegistered.current = true
        console.log('[useIsEvaluationReady] Registered data map observer', {
          dataMapExists: doc.share.has('data'),
          dataMapSize: dataMapToObserve.size,
          timestamp: new Date().toISOString(),
        })
      } catch (e) {
        console.warn(
          '[useIsEvaluationReady] Failed to register data map observer:',
          e
        )
      }
    }

    return () => {
      console.log('[useIsEvaluationReady] Cleanup running', {
        isUpdateHandlerRegistered: isUpdateHandlerRegistered.current,
        isSyncHandlerRegistered: isSyncHandlerRegistered.current,
        isDataMapObserverRegistered: isDataMapObserverRegistered.current,
        isSlateSubscriptionRegistered: isSlateSubscriptionRegistered.current,
        timestamp: new Date().toISOString(),
      })

      // Reset isReady to false when cleaning up handlers
      // This ensures that when new handlers are set up, a state change from false->true will trigger a re-render
      // This is critical for the createEvaluation flow where handlers get re-registered after cleanup
      if (
        isUpdateHandlerRegistered.current ||
        isSyncHandlerRegistered.current ||
        isDataMapObserverRegistered.current
      ) {
        console.log(
          '[useIsEvaluationReady] Resetting ready to false during cleanup (handlers were registered)'
        )
        setIsReady(false)
      }

      // Unsubscribe from Slate content changes
      if (
        isSlateSubscriptionRegistered.current &&
        slateUnsubscribeRef.current
      ) {
        console.log('[useIsEvaluationReady] Unsubscribing from Slate content')
        slateUnsubscribeRef.current()
        slateUnsubscribeRef.current = null
        isSlateSubscriptionRegistered.current = false
      }
      // Only remove update handler if it was actually registered
      // Use ref to get current doc in case it changed
      const currentDoc = docRef.current
      if (
        isUpdateHandlerRegistered.current &&
        updateHandlerRef.current &&
        currentDoc
      ) {
        console.log('[useIsEvaluationReady] Removing update handler')
        try {
          currentDoc.off('update', updateHandlerRef.current)
        } catch (e) {
          // Doc might be destroyed, ignore
        }
        isUpdateHandlerRegistered.current = false
        updateHandlerRef.current = null
      }

      // Only remove sync handler if it was actually registered
      // Use ref to get current provider in case it changed
      const currentProvider = providerRef.current
      if (
        currentProvider &&
        isSyncHandlerRegistered.current &&
        syncHandlerRef.current
      ) {
        console.log('[useIsEvaluationReady] Removing sync handler')
        try {
          currentProvider.off('sync', syncHandlerRef.current)
        } catch (e) {
          // Provider might be destroyed, ignore
        }
        isSyncHandlerRegistered.current = false
        syncHandlerRef.current = null
      }

      // Remove data map observer if it was registered
      // Reuse currentDoc from above
      if (
        currentDoc &&
        isDataMapObserverRegistered.current &&
        dataMapObserverRef.current
      ) {
        console.log('[useIsEvaluationReady] Removing data map observer')
        if (currentDoc.share.has('data')) {
          try {
            const currentDataMap = currentDoc.getMap('data')
            currentDataMap.unobserve(dataMapObserverRef.current)
          } catch (e) {
            // DataMap might be destroyed, ignore
          }
        }
        isDataMapObserverRegistered.current = false
        dataMapObserverRef.current = null
      }
    }
  }, [docGuid, providerRoom]) // Only re-run when doc/provider actually change (by guid/roomname), not when object references change

  return isReady
}

export function useEvaluationTable(tableType: string, tableName: string) {
  const { doc, provider } = useEvaluationContext()
  const tableKey = useTableKey(tableType, tableName)

  const yMap = useMemo(() => {
    if (tableKey && doc.share.has(tableKey)) {
      return doc.getMap(tableKey) as Y.Map<EvaluationDataTable>
    }
    return null
  }, [doc, tableKey])

  useEffect(() => {
    if (tableKey && yMap) {
      // TODO
      // Todd mentioned that the entire update tables thing is new.
      // It was added so that they would refresh after we added vendors, etc.
      // Come back to this if it's not serving the purpose and find the right place to call it
      updateTables(provider, doc, tableKey)
      bindEvaluationStoreTableToYMap(tableKey, tableName, yMap)
    }
  }, [tableKey, yMap, provider, doc, tableName])

  return useTableSnapshot(tableKey, tableName)
}

export function useAnalysisTable(tableType: string, tableName: string) {
  const { doc } = useEvaluationContext()
  const [updateCounter, setUpdateCounter] = useState(0)

  const tableKey = useTableKey(tableType, tableName)

  const yMap = useMemo(() => {
    if (tableKey && doc.share.has(tableKey)) {
      return doc.getMap(tableKey) as Y.Map<any>
    }
    return null
  }, [doc, tableKey])

  const data = yMap?.get('data') as Y.Array<EvaluationDataTable>
  const header = yMap?.get('header') as Y.Array<EvaluationDataTable>

  // Observe Y.js arrays to trigger re-renders when data changes
  useEffect(() => {
    if (!yMap) {
      return
    }

    const observer = () => {
      setUpdateCounter((prev) => prev + 1)
    }

    // Observe the map itself to catch when data/header arrays are added
    const mapObserver = () => {
      setUpdateCounter((prev) => prev + 1)
    }

    yMap.observe(mapObserver)

    if (data) {
      data.observe(observer)
    }
    if (header) {
      header.observe(observer)
    }

    return () => {
      yMap.unobserve(mapObserver)
      if (data) {
        data.unobserve(observer)
      }
      if (header) {
        header.unobserve(observer)
      }
    }
  }, [yMap, data, header])

  // Use updateCounter to ensure re-renders when data changes
  // This ensures we pick up initial data and subsequent updates
  return useMemo(() => {
    return {
      data: data?.toJSON(),
      header: header?.toJSON(),
    }
  }, [data, header, updateCounter])
}

export function useEvaluationComments() {
  const { doc } = useEvaluationContext()
  const yMap = useMemo(() => doc.getMap<TComment>('comments'), [doc])
  useEffect(() => {
    bindEvaluationStoreObjectToYObject(evaluationStore.comments.values, yMap)
  }, [yMap])
  return useSnapshot(evaluationStore.comments.values)
}

export function useEvaluationCommentsUsers() {
  const { doc } = useEvaluationContext()
  const yMap = useMemo(() => doc.getMap<CommentUser>('comments_users'), [doc])
  useEffect(() => {
    bindEvaluationStoreObjectToYObject(evaluationStore.comments.users, yMap)
  }, [yMap])
  return useSnapshot(evaluationStore.comments.users)
}

export function useEvaluationGates() {
  const { doc } = useEvaluationContext()
  const yMap = useMemo(() => doc.getMap<GateValue>('gates'), [doc])
  useEffect(() => {
    bindEvaluationStoreObjectToYObject(evaluationStore.gates, yMap)
  }, [yMap])
  return useSnapshot(evaluationStore.gates)
}
